import { Base64, DLType, VerifyType, verificationSessionPayload } from './types';

import { apiUrl, orchestrationAPIUrl, apiKey } from './envUtils';

const rand = () => Math.random().toString(36).substr(2);

export const postImages = (image: Base64) =>
  fetch(`${apiUrl}/upload-img`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ image }),
  });

/**
 * @category Api
 * @param pramUuid UUID to delete
 * @param callback Callback called once the delete is completed
 */
export async function deleteUuid(guid: string, callback: () => void) {
  const myHeaders = new Headers();
  myHeaders.append('Accept', 'application/json, text/plain, */*');
  myHeaders.append('Accept-Encoding', 'gzip, deflate, br');
  myHeaders.append('x-api-key', apiKey);

  const formdata = new FormData();
  formdata.append('guid', guid);
  formdata.append('api_key', apiKey);

  const requestOptions: RequestInit = {
    method: 'POST',
    headers: myHeaders,
    body: formdata,
    mode: 'no-cors',
  };

  try {
    await fetch(`${apiUrl}/deleteUser`, requestOptions);
    callback();
  } catch (e) {
    console.error(e);
  }
}

export async function getPhrases(): Promise<Array<string>> {
  const requestOptions: RequestInit = {
    method: 'POST',
  };

  const res = await fetch(`${apiUrl}/getLivenessPhrases`, requestOptions);

  return res.json();
}

export async function sendOTP(phone_number: string): Promise<string> {
  if (!phone_number) return '';

  const token = rand() + rand();

  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({
      phone_number,
      token,
      api_key: apiKey,
      hostURL: window.location.href,
      customerName: 'betmgm',
    }),
  };

  const res = await fetch(`${apiUrl}/sendOTP`, requestOptions);

  return res.json();
}

export async function verifyOTP(otp: string, token: string): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({
      token,
      otp,
    }),
  };

  const res = await fetch(`${apiUrl}/verifyOTP`, requestOptions);

  return res.json();
}

type createUserProps = {
  id?: string;
  guid?: string;
  phone?: string;
  phoneVerified?: boolean;
  email?: string;
  emailVerified?: boolean;
  ssn4?: string;
  ssn9?: string;
  userConsent: boolean;
  userConsentDate: string;
  userFaceEnrollConsent?: boolean;
  consentVersion?: string;
  consentContent?: string;
  portrait?: string;
};

export async function createUser(payload: createUserProps): Promise<string> {
  const userInfo = {
    token: payload.id,
    phone: payload.phone,
    phoneVerified: payload.phoneVerified,
    email: payload.email,
    emailVerified: payload.emailVerified,
    ssn: payload.ssn4,
    ssn4: payload.ssn4,
    ssn9: payload.ssn9,
    userConsent: payload.userConsent,
    userConsentDate: payload.userConsentDate,
    userFaceEnrollConsent: payload.userFaceEnrollConsent,
    consentVersion: payload.consentVersion,
    consentContent: payload.consentContent,
  };

  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(userInfo),
    headers: {
      'x-api-key': apiKey,
      'Content-Type': 'application/json',
    },
  };
  const res = await fetch(`${apiUrl}/user/create`, requestOptions);

  return res.json();
}

export async function sendEmail(email: string, uuid: string, skin: string, customerName: string): Promise<string> {
  const token = rand() + rand();
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({
      skin,
      customerName,
      alias: uuid,
      hostUrl: window.location.href,
      version: '1.0',
      api_key: apiKey,
      email_address: email,
      token,
      action: 'exact_url',
    }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/requestAuthLink`, requestOptions);

  return res.json();
}

export async function expandUri(shortUrl: string): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({
      shortUrl,
      api_key: apiKey,
    }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/expandUri`, requestOptions);

  return res.json();
}

type VerifyTokenProps = {
  code: string;
  type: VerifyType;
};
export async function verifyToken({ code, type }: VerifyTokenProps): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({ code, type }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/verif_code`, requestOptions);

  return res.json();
}

export async function sendSmsCode({ id, phone }: { id: string; phone: string }): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({ token: id, phone }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/send_code`, requestOptions);

  return res.json();
}

export async function sendEnrollSms({ id }: { id: string }): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({ token: id }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/send_redirectSms`, requestOptions);

  return res.json();
}

export async function sendMessage({
  id,
  type,
  endpoint,
  message,
  email,
  subject,
  phone,
}: {
  id: string;
  type: 'email' | 'phone';
  endpoint: string;
  message: string;
  email?: string;
  subject?: string;
  phone?: string;
}): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({ token: id, type, email, subject, phone, endpoint, message }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/sendMessage`, requestOptions);
  return res.json();
}

export async function uploadPortrait({ id, portrait }: { id: string; portrait: string }): Promise<string> {
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify({ token: id, portrait }),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/upload/portrait`, requestOptions);

  return res.json();
}

type UploadDlProps = {
  id: string;
  type: DLType;
  image?: string;
  barcode?: string;
};
export async function uploadDL({ id, type, image, barcode }: UploadDlProps): Promise<string> {
  let payload;

  if (type === DLType.BARCODEJSON) {
    payload = {
      token: id,
      type,
      barcode,
    };
  } else {
    payload = {
      token: id,
      type,
      image,
    };
  }

  if (type === DLType.BARCODEJSON && !barcode) throw new Error("Barcode data is required for 'barcodeJson' type.");
  else if (type !== DLType.BARCODEJSON && !image) throw new Error('Image is required.');

  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/upload/dl`, requestOptions);
  return res.json();
}

export async function getUserStatus({ id }: { id: string }): Promise<{
  complianceReview: boolean;
  expiredDate: string;
  expiredID: boolean;
  manualPIIEntry: boolean;
  orchestrationStatus: string;
  requestResAddress: boolean;
  requestSSN4: boolean;
  requestSSN9: boolean;
  requestScanID: boolean;
  toBeEncrypted: boolean;
  underAge: boolean;
  userApproved: boolean;
  userBiometric: string;
  userRegistered: boolean;
  userScanId: boolean;
}> {
  const payload = { token: id };
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/status`, requestOptions);
  return res.json();
}

type Attributes = {
  uuid?: string;
  guid?: string;
  ssn?: string;
  ssn4?: string;
  govId?: {
    firstName?: string;
    lastName?: string;
    portraitConfScore?: number;
    address?:
      | {
          addressLine1?: string;
          addressLine2?: string;
          city?: string;
          state?: string;
          zipCode?: string;
          country?: string;
        }
      | undefined;
  };
};

export async function updateUser({ id, attributes }: { id: string; attributes: Attributes }): Promise<string> {
  const payload = { token: id, attributes };
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${apiUrl}/user/update`, requestOptions);

  return res.json();
}

export async function verifyUser({
  id,
  apiValue,
  organizationApiKey,
}: {
  id: string;
  apiValue: string;
  organizationApiKey: string;
}): Promise<any> {
  const payload = { token: id };
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      x_api_key: organizationApiKey,
    },
  };

  const res = await fetch(`${orchestrationAPIUrl}/verify/${apiValue}`, requestOptions);
  return res.json();
}

export async function getProductGroups(): Promise<any> {
  const requestOptions: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  const res = await fetch(`${orchestrationAPIUrl}/product-group/list/prod`, requestOptions);
  return res.json();
}

export async function transmit({
  id,
  url,
  sessionToken,
  final,
  apiToken,
}: {
  id: string;
  url: string;
  sessionToken: string;
  apiToken?: string;
  final: boolean;
}) {
  const payload = { token: id, url, sessionToken, apiToken, final };
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  return fetch(`${orchestrationAPIUrl}/transmit`, requestOptions);
}

export async function validateSecurityToken({ token, uuid }: { token: string; uuid: string }) {
  const payload = { api_key: apiKey, uuid, securityToken: token, IAL: '1' };
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };
  const result = await (await fetch(`${apiUrl}/isSecurityTokenValid`, requestOptions)).json();
  return result;
}

export enum VerificationType {
  IDENTITY = 'IDENTITY',
  AGE = 'AGE',
}

export async function createVerificationSession({
  successUrl,
  failureUrl,
  type,
  productGroupId,
}: {
  successUrl: string;
  failureUrl: string;
  type: VerificationType;
  productGroupId: string;
}): Promise<any> {
  const payload = { successUrl, failureUrl, type, productGroupId };
  const requestOptions: RequestInit = {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };
  const result = await (await fetch(`${orchestrationAPIUrl}/verification-session’`, requestOptions)).json();

  return result;
}
